import InfiniteScroll from "react-infinite-scroll-component";
import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle,
} from "react";
import { AuthContext } from "../Auth/AuthProvider.js";
import axios from "axios";
import QuestionItem from "./QuestionItem.js";
import { backend_url } from "shared/config.js";

const PostsContainer = forwardRef(({ topic }, ref) => {
  const { user } = useContext(AuthContext);

  const [items, setItems] = useState([]);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [page, setPage] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh,
  }));

  useEffect(() => {
    setIsInitialLoading(true);
    axios
      .get(backend_url + "/get/questions?page=0&topic=" + topic, {
        headers: {
          Authorization: user?.uid,
        },
      })
      .then((res) => {
        if (res.data.length == 0) {
          setHasMorePosts(false);
        } else {
          setHasMorePosts(true);
        }

        setItems(res.data);
        setIsInitialLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsInitialLoading(false);
      });
  }, [topic]);

  function fetchData() {
    fetch(backend_url + "/get/questions?page=" + page + "&topic=" + topic, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: user?.uid,
      },
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.length == 0) {
          setHasMorePosts(false);
        } else {
          setHasMorePosts(true);
        }
        // Data structure = posts collection from DB
        setPage(page + 1);
        setItems((items) => [...items, ...data]);
      })
      .catch((err) => console.error(err));
  }

  function refresh() {
    setPage(1);
    setIsInitialLoading(true);
    axios
      .get(backend_url + "/get/questions?page=0&topic=" + topic, {
        headers: {
          Authorization: user?.uid,
        },
      })
      .then((res) => {
        if (res.data.length == 0) {
          setHasMorePosts(false);
        } else {
          setHasMorePosts(true);
        }
        setItems(res.data);
        setIsInitialLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsInitialLoading(false);
      });
  }

  const handleDeletePost = (postId) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== postId));
  };

  // Custom loader component
  const LoadingAnimation = () => (
    <div className="flex flex-col items-center justify-center py-8 w-full">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-indigo-200 border-solid rounded-full"></div>
        <div className="w-16 h-16 border-4 border-t-4 border-indigo-600 border-solid rounded-full absolute top-0 left-0 animate-spin"></div>
      </div>
      <div className="mt-4 text-indigo-600 font-medium">
        Loading conversations...
      </div>
      <div className="mt-2 w-16 h-1 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full animate-pulse"></div>
    </div>
  );

  // Initial full-screen loading state
  if (isInitialLoading) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-400 to-purple-500 blur-sm opacity-75 animate-pulse"></div>
            <div className="absolute inset-1 rounded-full bg-white"></div>
            <div className="absolute inset-3 rounded-full border-4 border-indigo-200"></div>
            <div className="absolute inset-3 rounded-full border-t-4 border-indigo-600 animate-spin"></div>
          </div>
          <p className="mt-6 text-lg font-medium text-indigo-700">
            Loading Abstruse
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Fetching conversations...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-hidden">
      <InfiniteScroll
        dataLength={items.length}
        next={fetchData}
        hasMore={hasMorePosts}
        loader={<LoadingAnimation />}
        endMessage={
          <p
            style={{ textAlign: "center" }}
            className="py-4 text-gray-500 font-medium"
          >
            <span className="inline-block border-b-2 border-indigo-300 pb-1">
              No more posts to show
            </span>
          </p>
        }
        className="space-y-4 overflow-x-hidden"
        refreshFunction={refresh}
        pullDownToRefresh
        pullDownToRefreshThreshold={50}
        pullDownToRefreshContent={
          <h3 style={{ textAlign: "center" }} className="text-indigo-500 py-2">
            &#8595; Pull down to refresh
          </h3>
        }
        releaseToRefreshContent={
          <h3 style={{ textAlign: "center" }} className="text-indigo-600 py-2">
            &#8593; Release to refresh
          </h3>
        }
      >
        <div className="w-full h-full mt-1 sm:mt-4 flex flex-col">
          {items?.map((item, index) => (
            <div className="p-4 bg-gray-100" key={index}>
              <QuestionItem
                key={item.id}
                post={item.post}
                postCreator={item.postCreator}
                answerers={item.answerers}
                add_reply={true}
                onDelete={handleDeletePost}
              />
            </div>
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
});

export default PostsContainer;
